import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { PersonalInfo } from '../types';

interface AboutMeLandingProps {
    personalInfo: PersonalInfo;
    stats?: {
        projectsCount: number;
        happyClients?: string;
        yearsOfExperience: number;
    };
}

const AboutMeLanding = ({ personalInfo, stats }: AboutMeLandingProps) => {
    const services = [
        { name: 'Frontend Development', icon: 'Code' },
        { name: 'React Development', icon: 'Atom' },
        { name: 'Responsive Web Development', icon: 'Monitor' },
        { name: 'AI Integration', icon: 'Cpu' },
        // { name: 'Graphics Design', icon: 'Figma' },
    ];

    return (
        <section className="relative py-24 bg-[#0C2C39] overflow-hidden" id="about-me">
            {/* Background Network Pattern */}
            <div className="absolute inset-0 z-0 opacity-[0.05] pointer-events-none"
                style={{
                    backgroundImage: 'radial-gradient(#FFF9EB 0.5px, transparent 0.5px)',
                    backgroundSize: '40px 40px'
                }} />

            <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
                <div className="grid lg:grid-cols-2 gap-16 items-start">
                    {/* Left: Services List with Vertical Markers */}
                    <div className="space-y-8 relative">
                        <div className="absolute left-[19px] top-6 bottom-6 w-0.5 border-l-2 border-dashed border-[#FFF9EB]/20" />

                        {services.map((service, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="flex items-center space-x-6 relative z-10"
                            >
                                <div className="w-10 h-10 rounded-lg bg-[#FFF9EB]/10 flex items-center justify-center border border-[#FFF9EB]/20 text-[#FFF9EB]">
                                    <Icon name={service.icon as any} size={20} />
                                </div>
                                <h3 className="text-2xl sm:text-3xl font-black text-[#FFF9EB] tracking-tight hover:text-teal-400 transition-colors">
                                    {service.name}
                                </h3>
                            </motion.div>
                        ))}
                    </div>

                    {/* Right: Content & Stats */}
                    <div className="space-y-12">
                        <div className="space-y-6">
                            <motion.h2
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                className="text-4xl sm:text-5xl font-black text-[#FFF9EB] tracking-tighter"
                            >
                                About me
                            </motion.h2>
                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.1 }}
                                className="text-lg text-[#FFF9EB]/70 leading-relaxed font-medium"
                            >
                                {personalInfo?.description || "I'm a full-stack mobile developer with over six years of experience crafting exceptional digital experiences, driven by a passion for innovation and a love for tackling complex challenges. My expertise spans the entire development lifecycle, from initial UI/UX design to robust backend development and seamless cross-platform mobile applications."}
                            </motion.p>
                        </div>

                        {/* Impact Counters */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 pt-6 border-t border-[#FFF9EB]/10">
                            {[
                                { label: 'Completed projects', value: `${stats?.projectsCount || '4'}+` },
                                { label: 'Satisfied Clients', value: stats?.happyClients || '100%' },
                                { label: 'Years of experience', value: `${stats?.yearsOfExperience || '2'}+` }
                            ].map((stat, i) => (
                                <div key={i} className="space-y-1">
                                    <div className="text-4xl sm:text-5xl font-black text-[#FFF9EB] tracking-tighter">
                                        {stat.value}
                                    </div>
                                    <div className="text-[10px] font-black ` tracking-[0.2em] text-[#FFF9EB]/40">
                                        {stat.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AboutMeLanding;
